import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  type RouteObject
} from 'react-router-dom'

import { CheckRegisterComponent } from '../components/CheckRegister/CheckRegisterComponent'
// import { Statistics } from '../components/FinancialComponent/Statistics'
import { ProcessingDashBoardSummary } from '../components/ProcessingDashboard/Summary'
import { SentryRouterIntegration } from '../components/sentry/sentry-router-integration'
import { Role } from '../domain/role.enum'
import { Home } from '../layout/home/home'
import { AuditLogsPage } from '../pages/audit-logs'
import { CAChecksPage } from '../pages/ca-checks'
import { CAEpayCheckRegistersPage } from '../pages/ca-epay-check-registers'
import { ClaimsPage } from '../pages/claims'
import { GroupsSchedulerPage } from '../pages/groups/scheduler/scheduler'
import { SFTPHealthcheck } from '../pages/healthcheck'
import { HolidaysListPage } from '../pages/holidays'
import { PinnacleFeedPage } from '../pages/integrations/pinnacle/feed'
import { PinnacleTpaPage } from '../pages/integrations/pinnacle/tpa'
import { PinnacleLockboxChecksPage } from '../pages/integrations/pinnacle/lockbox-checks'
import { LockboxesPage } from '../pages/integrations/pinnacle/lockboxes'
import { X937ChecksPage } from '../pages/integrations/pinnacle/x937-checks'
import { LockboxDetailsPage } from '../pages/integrations/pinnacle/lockbox-details'
import { PinnacleTransactionsPage } from '../pages/integrations/pinnacle/transactions'
import { LocationsListPage } from '../pages/locations'
import { NewGroupsPage } from '../pages/new-groups'
import { NotificationsPage } from '../pages/notifications'
import { ReleasePaymentFilesPage } from '../pages/payment-files/release'
import { PremiumPaymentsGroupManagementPage } from '../pages/premium/groups'
import { PremiumAchPullDetailPage } from '../pages/premium/pull-details'
import { PremiumAchPullsPage } from '../pages/premium/pulls'
import { RolePermissionsPage } from '../pages/role-permissions'
import { NotificationSettingsPage } from '../pages/settings/notifications'
import { PasskeySettingsPage } from '../pages/settings/security'
import { SFTPDiagnostic } from '../pages/sftp-diagnostic'
import { SFTPTreeView } from '../pages/sftp-tree-view'
import { UsersPage } from '../pages/users'
import { VendorsPage } from '../pages/vendors'
import { WebhookLogsPage } from '../pages/webhook-logs'
import { UIKitPage } from '../pages/uikit'

import { DefaultRedirect } from './default-redirect'
import { Guard } from './guard'
import { TemporalDiagnostic } from '../pages/temporal'
import { SocketDebugPage } from '../pages/socket-debug'

export function Root(): React.ReactNode {
  return (
    <SentryRouterIntegration>
      <Outlet />
    </SentryRouterIntegration>
  )
}

const routeElements = (
  <Route element={<Root />}>
    <Route element={<Home />}>
      <Route index element={<Guard element={<DefaultRedirect />} />} />

      <Route path='integrations'>
        <Route path='pinnacle'>
          <Route
            path='feed'
            element={
              <Guard
                element={<PinnacleFeedPage />}
                roles={[Role.Administrator, Role.FinanceManager, Role.EPayablesManager]}
              />
            }
          />
          <Route
            path='tpa'
            element={
              <Guard
                element={<PinnacleTpaPage />}
                roles={[Role.Administrator, Role.FinanceManager]}
              />
            }
          />
          <Route
            path='lockbox-checks'
            element={
              <Guard
                element={<PinnacleLockboxChecksPage />}
                roles={[Role.Administrator, Role.FinanceManager]}
              />
            }
          />
          <Route
            path='lockbox/:id'
            element={
              <Guard
                element={<LockboxDetailsPage />}
                roles={[Role.Administrator, Role.FinanceManager]}
              />
            }
          />
          <Route
            path='lockboxes'
            element={
              <Guard
                element={<LockboxesPage />}
                roles={[Role.Administrator, Role.FinanceManager]}
              />
            }
          />
          <Route
            path='x937-checks'
            element={
              <Guard
                element={<X937ChecksPage />}
                roles={[Role.Administrator, Role.FinanceManager]}
              />
            }
          />
          <Route
            path='transactions'
            element={
              <Guard
                element={<PinnacleTransactionsPage />}
                roles={[Role.Administrator, Role.FinanceManager]}
              />
            }
          />
        </Route>
      </Route>

      <Route path='admin'>
        <Route path='users'>
          <Route
            index
            element={
              <Guard
                element={<UsersPage />}
                roles={[
                  Role.Administrator,
                  Role.FinanceManager,
                  Role.CheckRunManager,
                  Role.AccountAdministrator
                ]}
              />
            }
          />
        </Route>
        <Route
          path='sftp-tree-view'
          element={<Guard element={<SFTPTreeView />} roles={[Role.Administrator]} />}
        />
      </Route>
      <Route path='register'>
        <Route
          path='claims'
          element={
            <Guard
              element={<ClaimsPage />}
              roles={[
                Role.Administrator,
                Role.FinanceManager,
                Role.CheckRunManager,
                Role.CheckRun,
                Role.CallCenter,
                Role.AccountAdministrator,
                Role.AccountExecutive,
                Role.AccountManager
              ]}
            />
          }
        />
        <Route path='release-payment-files'>
          <Route
            index
            element={
              <Guard
                element={<ReleasePaymentFilesPage />}
                roles={[
                  Role.Administrator,
                  Role.CheckRunManager,
                  Role.CheckRun,
                  Role.EPayablesManager
                ]}
              />
            }
          />
        </Route>
        <Route
          path='check-registers'
          element={
            <Guard
              element={<CheckRegisterComponent />}
              roles={[
                Role.Administrator,
                Role.CheckRunManager,
                Role.CheckRun,
                Role.AccountExecutive,
                Role.AccountAdministrator,
                Role.AccountManager,
                Role.EPayablesManager
              ]}
            />
          }
        />
      </Route>
      <Route path='groups'>
        <Route
          index
          element={
            <Guard
              element={<NewGroupsPage />}
              roles={[Role.Administrator, Role.FinanceManager, Role.CheckRunManager]}
            />
          }
        />
        <Route
          path='scheduler'
          element={
            <Guard
              element={<GroupsSchedulerPage />}
              roles={[Role.Administrator, Role.CheckRunManager, Role.CheckRun, Role.FinanceManager]}
            />
          }
        />
      </Route>
      <Route path='premium'>
        <Route path='groups'>
          <Route
            index
            element={
              <Guard
                element={<PremiumPaymentsGroupManagementPage />}
                roles={[Role.Administrator, Role.FinanceManager]}
              />
            }
          />
        </Route>
        <Route path='pulls'>
          <Route
            index
            element={
              <Guard
                element={<PremiumAchPullsPage />}
                roles={[Role.Administrator, Role.FinanceManager]}
              />
            }
          />
        </Route>
        <Route path='pull'>
          <Route
            path=':id'
            element={
              <Guard
                element={<PremiumAchPullDetailPage />}
                roles={[Role.Administrator, Role.FinanceManager]}
              />
            }
          />
        </Route>
        <Route path='vendors'>
          <Route
            index
            element={
              <Guard element={<VendorsPage />} roles={[Role.Administrator, Role.FinanceManager]} />
            }
          />
        </Route>
      </Route>
      <Route path='uikit'>
        <Route index element={<Guard element={<UIKitPage />} />} />
      </Route>
      <Route path='dashboard'>
        <Route
          path='summary'
          element={
            <Guard
              element={<ProcessingDashBoardSummary />}
              roles={[
                Role.Administrator,
                Role.FinanceManager,
                Role.CheckRunManager,
                Role.CheckRun,
                Role.EPayablesManager
              ]}
            />
          }
        />
        {/* <Route
          path='statistics'
          element={<Guard element={<Statistics />} roles={Role.Administrator} />}
        /> */}
      </Route>
      <Route path='locations'>
        <Route
          index
          element={
            <Guard
              element={<LocationsListPage />}
              roles={[Role.Administrator, Role.CheckRunManager, Role.CheckRun]}
            />
          }
        />
      </Route>
      <Route path='holidays'>
        <Route
          index
          element={
            <Guard
              element={<HolidaysListPage />}
              roles={[Role.Administrator, Role.CheckRunManager, Role.CheckRun]}
            />
          }
        />
      </Route>
      <Route path='healthcheck'>
        <Route
          index
          element={
            <Guard
              element={<SFTPHealthcheck />}
              roles={[Role.Administrator, Role.CheckRunManager, Role.CheckRun]}
            />
          }
        />
      </Route>
      <Route path='settings'>
        <Route path='security' element={<Guard element={<PasskeySettingsPage />} />} />
        <Route path='notifications'>
          <Route
            index
            element={
              <Guard
                element={<NotificationSettingsPage />}
                roles={[
                  Role.Administrator,
                  Role.FinanceManager,
                  Role.CheckRunManager,
                  Role.CheckRun
                ]}
              />
            }
          />
        </Route>
      </Route>
      <Route path='notifications'>
        <Route
          index
          element={
            <Guard
              element={<NotificationsPage />}
              roles={[
                Role.Administrator,
                Role.FinanceManager,
                Role.CheckRunManager,
                Role.AccountAdministrator,
                Role.CheckRun,
                Role.AccountExecutive,
                Role.AccountManager,
                Role.EPayablesManager
              ]}
            />
          }
        />
      </Route>
      <Route path='audit-logs'>
        <Route index element={<Guard element={<AuditLogsPage />} roles={[Role.Administrator]} />} />
      </Route>
      <Route path='ca'>
        <Route
          path='checks'
          element={<Guard element={<CAChecksPage />} roles={[Role.Administrator]} />}
        />
        <Route
          path='epay/check-registers'
          element={<Guard element={<CAEpayCheckRegistersPage />} roles={[Role.Administrator]} />}
        />
      </Route>
      <Route path='webhook-logs'>
        <Route
          index
          element={<Guard element={<WebhookLogsPage />} roles={[Role.Administrator]} />}
        />
      </Route>
      <Route path={'role-permissions'}>
        <Route
          index
          element={<Guard element={<RolePermissionsPage />} roles={[Role.Administrator]} />}
        />
      </Route>
      <Route path='diagnostic'>
        <Route
          path='sftp'
          element={<Guard element={<SFTPDiagnostic />} roles={[Role.Administrator]} />}
        />
        <Route
          path='temporal'
          element={<Guard element={<TemporalDiagnostic />} roles={[Role.Administrator]} />}
        />
        <Route
          path='socket'
          element={<Guard element={<SocketDebugPage />} roles={[Role.Administrator]} />}
        />
      </Route>
    </Route>

    <Route path='*' element={<h1>Not found</h1>} />
  </Route>
)

export const routes: Array<RouteObject> = createRoutesFromElements(routeElements)

export const router = createBrowserRouter(routes)
